import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type Column,
} from "@legal-500/legal500-design-system";
import {
  LIST_KIND_LABELS,
  LIST_KINDS,
  powerlistLists,
  type ListKind,
  type PowerlistList,
} from "../../../api/powerlist.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import {
  ConfirmModal,
  FormError,
  errorMessage,
  parseSortOrder,
  textOrNull,
  usePowerlistAccess,
  useToast,
} from "../shared";
import styles from "../Powerlist.module.css";

type ListFormModalProps = {
  isOpen: boolean;
  editionId: string;
  list: PowerlistList | null;
  availableKinds: ListKind[];
  onClose: () => void;
  onSaved: (list: PowerlistList) => void;
};

function ListFormModal({
  isOpen,
  editionId,
  list,
  availableKinds,
  onClose,
  onSaved,
}: ListFormModalProps) {
  const [kind, setKind] = useState<ListKind | "">("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setKind(list?.kind ?? availableKinds[0] ?? "");
    setTitle(list?.title ?? "");
    setDescription(list?.description ?? "");
    setSortOrder(list ? String(list.sortOrder) : "");
    setError(null);
    setSaving(false);
  }, [isOpen, list, availableKinds]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const order = parseSortOrder(sortOrder);
    if (!kind) {
      setError("Choose a kind of list");
      return;
    }
    if (order === "invalid") {
      setError("Sort order must be a whole number of 0 or more");
      return;
    }
    setSaving(true);
    setError(null);
    const fields = {
      title: title.trim() || undefined,
      description: textOrNull(description),
      sortOrder: order,
    };
    try {
      onSaved(
        list
          ? await powerlistLists.update(list.id, fields)
          : await powerlistLists.create(editionId, { kind, ...fields }),
      );
    } catch (saveError) {
      setError(errorMessage(saveError));
      setSaving(false);
    }
  }

  const kindOptions = (list ? [list.kind] : availableKinds).map(value => ({
    value,
    label: LIST_KIND_LABELS[value],
  }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={list ? "Edit list" : "New list"}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormError message={error} />
        <Select
          id="powerlist-list-kind"
          label="Kind"
          value={kind}
          onChange={event => setKind(event.target.value as ListKind)}
          options={kindOptions}
          disabled={list !== null}
          fullWidth
        />
        <Input
          id="powerlist-list-title"
          label="Title"
          value={title}
          onChange={event => setTitle(event.target.value)}
          helperText={list ? undefined : "Leave blank to use the kind's name"}
          fullWidth
        />
        <Textarea
          id="powerlist-list-description"
          label="Description"
          value={description}
          onChange={event => setDescription(event.target.value)}
          rows={3}
          fullWidth
        />
        <Input
          id="powerlist-list-order"
          label="Sort order"
          type="number"
          min={0}
          inputMode="numeric"
          value={sortOrder}
          onChange={event => setSortOrder(event.target.value)}
          fullWidth
        />
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={saving}>
            {saving ? "Saving..." : list ? "Save" : "Create list"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type FormState = { open: false } | { open: true; list: PowerlistList | null };

type ListsTabProps = {
  editionId: string;
  lists: PowerlistList[];
  onChanged: () => Promise<void>;
};

export function ListsTab({ editionId, lists, onChanged }: ListsTabProps) {
  const { canWrite, canDelete } = usePowerlistAccess();
  const [form, setForm] = useState<FormState>({ open: false });
  const [removing, setRemoving] = useState<PowerlistList | null>(null);
  const { toast, success } = useToast();
  const availableKinds = useMemo(() => {
    const used = new Set(lists.map(list => list.kind));
    return LIST_KINDS.filter(kind => !used.has(kind));
  }, [lists]);

  const columns: Column<PowerlistList>[] = [
    { header: "Order", accessor: "sortOrder" },
    {
      header: "Kind",
      accessor: "kind",
      render: (_value: unknown, row: PowerlistList) => LIST_KIND_LABELS[row.kind],
    },
    { header: "Title", accessor: "title" },
    {
      header: "Description",
      accessor: "description",
      wrap: true,
      render: (_value: unknown, row: PowerlistList) => row.description ?? "—",
    },
    ...(canWrite || canDelete
      ? [
          {
            header: "Actions",
            accessor: "id" as const,
            render: (_value: unknown, row: PowerlistList) => (
              <div className={styles.actions}>
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit ${row.title}`}
                    onClick={() => setForm({ open: true, list: row })}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    aria-label={`Delete ${row.title}`}
                    onClick={() => setRemoving(row)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <p className={styles.muted}>Each list is a tab on the public page.</p>
        {canWrite && availableKinds.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setForm({ open: true, list: null })}
          >
            New list
          </Button>
        )}
      </div>
      <DataTable
        data={lists}
        columns={columns}
        rowKey="id"
        emptyMessage="This edition has no lists yet."
      />
      <ListFormModal
        isOpen={form.open}
        editionId={editionId}
        list={form.open ? form.list : null}
        availableKinds={availableKinds}
        onClose={() => setForm({ open: false })}
        onSaved={saved => {
          setForm({ open: false });
          success(`${saved.title} saved`);
          void onChanged();
        }}
      />
      <ConfirmModal
        isOpen={removing !== null}
        title="Delete list"
        message={`Delete ${removing?.title ?? "this list"} with its entries and list sponsors?`}
        confirmLabel="Delete"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          await powerlistLists.remove(removing.id);
          setRemoving(null);
          success("List deleted");
          await onChanged();
        }}
      />
      {toast}
    </div>
  );
}
